import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  consumeMagicLink,
  openBillingPortal,
  requestMagicLink,
} from "../lib/api";

export function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const checkoutSuccess = params.get("checkout") === "success";

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState<string | null>(null);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);

  useEffect(() => {
    setSession(localStorage.getItem("session"));
    setSignedInEmail(localStorage.getItem("email"));
  }, []);

  // Magic-link consumption flow
  useEffect(() => {
    if (!token) return;
    setBusy(true);
    consumeMagicLink(token)
      .then(({ session, email }) => {
        localStorage.setItem("session", session);
        localStorage.setItem("email", email);
        // Full reload so Header picks up signed-in state
        window.location.href = "/";
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Invalid or expired link");
        setBusy(false);
      });
  }, [token, navigate]);

  if (token) {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center space-y-3">
        {busy && <p>Signing you in…</p>}
        {error && <p className="text-red-600">{error}</p>}
      </div>
    );
  }

  // Already signed in → show account / portal
  if (session) {
    const openPortal = async (flow?: "cancel") => {
      setBusy(true);
      setError(null);
      try {
        const { url } = await openBillingPortal(session, flow);
        window.location.href = url;
      } catch (e) {
        const msg =
          e && typeof e === "object" && "error" in e
            ? (e as { error: string }).error
            : e instanceof Error
              ? e.message
              : "Could not open billing portal";
        setError(msg);
        setBusy(false);
      }
    };
    const onSignOut = () => {
      localStorage.removeItem("session");
      localStorage.removeItem("email");
      window.location.href = "/";
    };
    return (
      <div className="max-w-md mx-auto px-6 py-16 space-y-6">
        <h1 className="text-2xl font-semibold">Account</h1>
        <p className="text-ink/70">
          Signed in as <strong>{signedInEmail}</strong>
        </p>
        <div className="space-y-3">
          <button
            onClick={() => openPortal()}
            disabled={busy}
            className="btn-primary w-full justify-center"
          >
            {busy ? "Opening…" : "Manage subscription"}
          </button>
          <button
            onClick={() => openPortal("cancel")}
            disabled={busy}
            className="w-full px-4 py-2 rounded-md border border-ink/15 text-sm hover:bg-ink/5"
          >
            Cancel subscription
          </button>
          <button
            onClick={onSignOut}
            className="w-full px-4 py-2 rounded-md text-sm text-ink/60 hover:text-ink"
          >
            Sign out
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="space-y-2 text-sm text-ink/60">
          <p>
            <strong>Manage subscription</strong> — update payment method, view
            invoices, change billing details.
          </p>
          <p>
            <strong>Cancel subscription</strong> — instant; access continues
            until the end of the current billing period.
          </p>
          <p>
            <strong>Refund?</strong> Email{" "}
            <a
              href="mailto:support@chart-to-csv.com"
              className="underline"
            >
              support@chart-to-csv.com
            </a>{" "}
            — we issue full refunds within 14 days of your first charge,
            no questions.
          </p>
        </div>
      </div>
    );
  }

  // Not signed in
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestMagicLink(email);
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to send link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 space-y-6">
      <h1 className="text-2xl font-semibold">Sign in</h1>
      {checkoutSuccess && !sent && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          Subscription created — enter the same email below and we'll send you
          a sign-in link.
        </div>
      )}
      {sent ? (
        <p className="text-ink/70">
          Magic link sent to <strong>{email}</strong>. Check your inbox.
          The link is valid for 15 minutes.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-3">
          <p className="text-ink/70 text-sm">
            Enter the email tied to your subscription. We'll send you a one-time
            sign-in link — no password.
          </p>
          <input
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-ink/15"
          />
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center">
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </form>
      )}
    </div>
  );
}
